/**
 * Shared Hyperliquid /info client: rate limiting, queue, and POST helper.
 * Extracted so domain modules (fills, candles, portfolio) can split without circular deps.
 */

export const HL_API_URL = 'https://api.hyperliquid.xyz';

/** HIP-3 perp-dex identifiers. xyz = USDC-collateral, cash = USDT. Matches backend DEFAULT_HIP3_DEXS. */
export const HIP3_DEXS = ['xyz', 'cash'] as const;

const HL_INFO_MIN_INTERVAL_MS = 250;
const HL_INFO_COOLDOWN_MS = 15_000;
let hlInfoCooldownUntilMs = 0;
let hlInfoLastRequestAtMs = 0;

/** True while Hyperliquid /info is in a post-429 cooldown window. */
export function isHlInfoRateLimited(): boolean {
  return Date.now() < hlInfoCooldownUntilMs;
}

type HlInfoQueuedTask = {
  fn: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  priority: boolean;
};

const hlInfoQueue: HlInfoQueuedTask[] = [];
let hlInfoPumpRunning = false;

function enqueueHlInfo<T>(fn: () => Promise<T>, opts?: { priority?: boolean }): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const task: HlInfoQueuedTask = {
      fn: fn as () => Promise<unknown>,
      resolve: resolve as (value: unknown) => void,
      reject,
      priority: opts?.priority === true,
    };
    if (task.priority) {
      const firstNormal = hlInfoQueue.findIndex((t) => !t.priority);
      if (firstNormal === -1) hlInfoQueue.push(task);
      else hlInfoQueue.splice(firstNormal, 0, task);
    } else {
      hlInfoQueue.push(task);
    }
    void pumpHlInfoQueue();
  });
}

async function pumpHlInfoQueue(): Promise<void> {
  if (hlInfoPumpRunning) return;
  hlInfoPumpRunning = true;
  while (hlInfoQueue.length > 0) {
    const task = hlInfoQueue.shift()!;
    try {
      task.resolve(await task.fn());
    } catch (err) {
      task.reject(err);
    }
  }
  hlInfoPumpRunning = false;
}

export async function hlInfoPost<T = unknown>(body: object, retryOn429 = true, priority = false): Promise<T> {
  return enqueueHlInfo(async () => {
    const now = Date.now();
    const cooldownWait = Math.max(0, hlInfoCooldownUntilMs - now);
    const spacingWait = Math.max(0, HL_INFO_MIN_INTERVAL_MS - (now - hlInfoLastRequestAtMs));
    const waitMs = Math.max(cooldownWait, spacingWait);
    if (waitMs > 0) {
      await new Promise((r) => setTimeout(r, waitMs));
    }

    hlInfoLastRequestAtMs = Date.now();
    const res = await fetch(`${HL_API_URL}/info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 429) {
      hlInfoCooldownUntilMs = Date.now() + HL_INFO_COOLDOWN_MS;
      if (retryOn429) {
        const retryAfter = res.headers.get('retry-after') || res.headers.get('Retry-After');
        const secs = retryAfter ? Math.min(60, Math.max(1, Number(retryAfter) || 3)) : 3;
        await new Promise((r) => setTimeout(r, secs * 1000));
        return hlInfoPost(body, false);
      }
      throw new Error('HL /info rate limited');
    }

    if (!res.ok) throw new Error(`HL /info ${res.status} ${res.statusText}`);
    return (await res.json()) as T;
  }, { priority });
}
